import { BaseWidget } from '@theia/core/lib/browser';
import { FileService } from '@theia/filesystem/lib/browser/file-service';
import { inject, injectable } from '@theia/core/shared/inversify';
import URI from '@theia/core/lib/common/uri';
import { FileContent } from '@theia/filesystem/lib/common/files';

export type ImagePreviewOptions = {
    uri: string;
};

export const MIME_TYPES: Record<string, string> = {
    '.png': 'image/png',
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.gif': 'image/gif',
    '.bmp': 'image/bmp',
    '.webp': 'image/webp',
    '.svg': 'image/svg+xml'
};

type ImagePreviewViewMode = 'fit' | 'original';

@injectable()
export class ImagePreviewWidget extends BaseWidget {
    static readonly ID: string = 'image-preview';

    @inject(FileService)
    protected readonly fileService!: FileService;

    protected objectUrl: string | undefined;
    protected viewport!: HTMLDivElement;
    protected stage!: HTMLDivElement;
    protected content!: HTMLDivElement;
    protected img!: HTMLImageElement;
    protected label!: HTMLDivElement;
    protected resizeObserver?: ResizeObserver;
    protected viewMode: ImagePreviewViewMode = 'fit';
    protected naturalWidth = 0;
    protected naturalHeight = 0;
    protected pendingFitAfterAnimation = false;

    initialize(options: ImagePreviewOptions): void {
        const uri: URI = new URI(options.uri);
        this.id = `${ImagePreviewWidget.ID}:${options.uri}`;
        this.title.label = uri.path.base;
        this.title.caption = options.uri;
        this.title.closable = true;
        this.title.iconClass = 'codicon codicon-file-media';
        this.node.classList.add('image-preview-widget');
        this.renderLayout();
        this.loadImage(uri);
    }

    protected renderLayout(): void {
        this.viewport = document.createElement('div');
        this.viewport.className = 'image-preview-viewport is-fit';

        this.stage = document.createElement('div');
        this.stage.className = 'image-preview-stage';

        this.content = document.createElement('div');
        this.content.className = 'image-preview-content';

        this.img = document.createElement('img');
        this.img.draggable = false;
        this.img.addEventListener('click', () => this.toggleZoom());
        this.img.addEventListener('transitionend', (event: TransitionEvent) => this.onImageTransitionEnd(event));

        this.label = document.createElement('div');
        this.label.className = 'image-preview-label';

        this.content.append(this.img, this.label);
        this.stage.appendChild(this.content);
        this.viewport.appendChild(this.stage);
        this.node.appendChild(this.viewport);

        this.resizeObserver = new ResizeObserver(() => {
            if (this.viewMode === 'fit') this.applyView();
            this.updateInteraction();
        });
        this.resizeObserver.observe(this.viewport);
    }

    protected async loadImage(uri: URI): Promise<void> {
        try {
            const content: FileContent = await this.fileService.readFile(uri);
            const mimeType: string = MIME_TYPES[uri.path.ext.toLowerCase()] ?? 'application/octet-stream';
            const byteLength: number = content.value.byteLength;
            const blob: Blob = new Blob([content.value.buffer.slice(0)], { type: mimeType });
            this.objectUrl = URL.createObjectURL(blob);
            this.img.src = this.objectUrl;
            this.img.onload = () => {
                this.naturalWidth = this.img.naturalWidth || this.img.width;
                this.naturalHeight = this.img.naturalHeight || this.img.height;
                const sizeInKB: number = Math.round(byteLength / 1024);
                this.label.textContent = `${this.naturalWidth}x${this.naturalHeight} (${sizeInKB}KB)`;
                this.setViewMode('fit');
            };
            this.img.onerror = () => {
                this.label.textContent = `Failed to display image: ${uri.path.base}`;
            };
        } catch (error: unknown) {
            this.node.textContent = `Failed to load image: ${error instanceof Error ? error.message : String(error)}`;
        }
    }

    protected toggleZoom(): void {
        const zoomable: boolean = this.imageExceedsViewport();
        if (!zoomable) return;
        this.setViewMode(this.viewMode === 'fit' ? 'original' : 'fit', true);
    }

    protected imageExceedsViewport(): boolean {
        if (!this.naturalWidth || !this.naturalHeight) return false;
        const { width, height } = this.viewport.getBoundingClientRect();
        return this.naturalWidth > width || this.naturalHeight > height;
    }

    protected computeFitDimensions(): { width: number; height: number } {
        const viewportRect: DOMRect = this.viewport.getBoundingClientRect();
        const stageStyle: CSSStyleDeclaration = getComputedStyle(this.stage);
        const contentStyle: CSSStyleDeclaration = getComputedStyle(this.content);
        const stagePaddingX: number = parseFloat(stageStyle.paddingLeft) + parseFloat(stageStyle.paddingRight);
        const stagePaddingY: number = parseFloat(stageStyle.paddingTop) + parseFloat(stageStyle.paddingBottom);
        const contentPaddingX: number = parseFloat(contentStyle.paddingLeft) + parseFloat(contentStyle.paddingRight);
        const contentPaddingY: number = parseFloat(contentStyle.paddingTop) + parseFloat(contentStyle.paddingBottom);
        const gap: number = parseFloat(contentStyle.rowGap) || parseFloat(contentStyle.gap) || 0;
        const labelHeight: number = this.label.offsetHeight;
        const availableWidth: number = viewportRect.width - stagePaddingX - contentPaddingX;
        const availableHeight: number = viewportRect.height - stagePaddingY - contentPaddingY - labelHeight - gap;
        const scale: number = Math.min(availableWidth / this.naturalWidth, availableHeight / this.naturalHeight, 1);
        return {
            width: Math.max(1, Math.round(this.naturalWidth * scale)),
            height: Math.max(1, Math.round(this.naturalHeight * scale))
        };
    }

    protected setViewMode(mode: ImagePreviewViewMode, animate = false): void {
        this.viewMode = mode;
        const shouldAnimate: boolean = animate && !this.prefersReducedMotion();
        this.pendingFitAfterAnimation = shouldAnimate && mode === 'fit';

        if (shouldAnimate && mode === 'original') {
            this.viewport.classList.remove('is-fit');
            this.viewport.classList.add('is-original');
        }

        this.applyView(shouldAnimate);
    }

    protected prefersReducedMotion(): boolean {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    protected applyView(animate = false): void {
        const dimensions: { width: number; height: number } =
            this.viewMode === 'fit'
                ? this.computeFitDimensions()
                : { width: this.naturalWidth, height: this.naturalHeight };

        this.img.classList.toggle('is-animated', animate);
        this.img.style.width = `${dimensions.width}px`;
        this.img.style.height = `${dimensions.height}px`;

        if (!animate) {
            this.syncViewportMode();
        }

        this.updateInteraction();
    }

    protected syncViewportMode(): void {
        const isFit = this.viewMode === 'fit';
        this.viewport.classList.toggle('is-fit', isFit);
        this.viewport.classList.toggle('is-original', !isFit);

        if (isFit) {
            this.viewport.scrollLeft = 0;
            this.viewport.scrollTop = 0;
        }
    }

    protected onImageTransitionEnd(event: TransitionEvent): void {
        if (event.target !== this.img || event.propertyName !== 'width') return;

        this.img.classList.remove('is-animated');

        if (this.pendingFitAfterAnimation) {
            this.pendingFitAfterAnimation = false;
            this.syncViewportMode();
        }
    }

    protected updateInteraction(): void {
        const zoomable: boolean = this.imageExceedsViewport();
        this.viewport.classList.toggle('is-zoomable', zoomable);

        if (!zoomable) {
            this.img.title = '';
            return;
        }

        this.img.title = this.viewMode === 'fit' ? 'Show original size' : 'Fit to viewport';
    }

    override dispose(): void {
        this.resizeObserver?.disconnect();
        this.resizeObserver = undefined;
        if (this.objectUrl) {
            URL.revokeObjectURL(this.objectUrl);
            this.objectUrl = undefined;
        }
        super.dispose();
    }
}
